import { LightGallery, LightGalleryItem } from '@lightgallery/react';
import Thumbnail from '@lightgallery/react/plugins/thumbnail';
import Zoom from '@lightgallery/react/plugins/zoom';
import { memo, useMemo } from 'react';

// import styles
import 'lightgallery/css/lg-thumbnail.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lightgallery.css';

type Props = {
  className?: string;
  attachments: {
    id: string;
    kind: string;
    thumb: string;
    url: string;
    originalFilename: string;
  }[];
};

function canDownload(id: string) {
  return !id.includes('cloudfront');
}

/**
 * For compatibility with the old simple attachment format
 */
function extractImageId(id: string) {
  if (id.startsWith('https://imagedelivery.net/')) {
    const match = id.match(/^https:\/\/imagedelivery\.net\/[^/]+\/([^/]+)\//);
    return match && match[1] ? match[1] : id;
  }
  return id;
}

export function Gallery({ className, attachments }: Props) {
  // Deduplicate attachments by id to prevent React key collision warnings
  // This handles edge cases where duplicate attachments may exist in data
  const uniqueAttachments = useMemo(() => {
    const seen = new Set<string>();
    return attachments.filter((att) => {
      if (seen.has(att.id)) return false;
      seen.add(att.id);
      return true;
    });
  }, [attachments]);
  // XXX Why is this still here? Something is probably broken!
  // FIXME
  // const uppy = useAppContext().uppy.get('image');
  // const withLocalPreviews = useMemo(() => {
  //   const localFiles = uppy.getFiles();
  //   return [...attachments].map((attachment) => {
  //     const localFile = localFiles.find(
  //       (file) =>
  //         file.meta.id &&
  //         typeof file.meta.id === 'string' &&
  //         attachment.id.includes(file.meta.id)
  //     );
  //     if (localFile) {
  //       return {
  //         ...attachment,
  //         thumb: localFile.preview,
  //         url: localFile.preview,
  //       };
  //     }
  //     return attachment;
  //   });
  // }, [attachments]);

  return (
    <div className={className || 'mb-2'}>
      <LightGallery
        className='grid w-full grid-cols-2 gap-2 xl:grid-cols-4'
        speed={500}
        plugins={[Thumbnail, Zoom]}
        licenseKey={import.meta.env.VITE_LIGHTGALLERY_LICENSE_KEY}
      >
        {uniqueAttachments.map((attachment) =>
          attachment.kind === 'image' ? (
            <LightGalleryItem
              key={attachment.id}
              item={{
                src: attachment.url,
                thumb: attachment.thumb,
                alt: attachment.originalFilename,
                downloadUrl: canDownload(attachment.id)
                  ? `/api/download/image/${extractImageId(attachment.id)}`
                  : false,
              }}
              href={attachment.url}
              className='block overflow-hidden bg-grey-5 pt-[100%]'
            >
              <img
                className='-mt-[100%] w-full'
                alt={attachment.originalFilename}
                src={attachment.thumb}
                onError={(event) => {
                  console.log('failed to display img', attachment.id);
                  event.currentTarget.closest('a')?.classList.add('hidden');
                }}
              />
            </LightGalleryItem>
          ) : null
        )}
      </LightGallery>
    </div>
  );
}

export const MemoGallery = memo(Gallery);
